import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ChevronLeft, ChevronRight, Eye, Ear, Brain } from 'lucide-react'

interface Etapa9SensorialProps {
  formData: any
  onChange: (field: string, value: any) => void
  onNext: () => void
  onPrev: () => void
  onSubmit?: () => void
  saving?: boolean
  submitting?: boolean
}

export function Etapa9Sensorial({ 
  formData, 
  onChange, 
  onNext, 
  onPrev, 
  onSubmit, 
  saving = false, 
  submitting = false 
}: Etapa9SensorialProps) {
  const sensorial = formData.sensorial || {}

  const handleSensorialChange = (field: string, value: boolean) => {
    onChange('sensorial', {
      ...sensorial,
      [field]: value
    })
  }

  // Função para verificar se pelo menos uma opção foi selecionada em cada categoria
  const hasSelectedInCategory = (options: string[]) => {
    return options.some(option => sensorial[option] === true)
  }

  // Função de validação com feedback
  const validateAndProceed = () => {
    const errors: string[] = []

    // Validar declaração de veracidade
    if (!formData.veracidade_etapa9) {
      errors.push('Declaração de veracidade')
    }

    // Validar visão
    if (!hasSelectedInCategory([
      'usa_oculos',
      'condicoes_visuais',
      'sensibilidade_luz',
      'visao_noturna',
      'nenhuma_opcao_visao'
    ])) {
      errors.push('Selecione pelo menos uma opção em Visão')
    }

    // Validar audição
    if (!hasSelectedInCategory([
      'usa_aparelho_auditivo',
      'dificuldade_ouvir',
      'zumbidos',
      'nenhuma_opcao_auditiva'
    ])) {
      errors.push('Selecione pelo menos uma opção em Audição')
    }

    // Validar paladar/olfato
    if (!hasSelectedInCategory([
      'usa_protese_dentaria',
      'alteracoes_paladar',
      'perda_olfato',
      'nenhuma_opcao_paladar_olfato'
    ])) {
      errors.push('Selecione pelo menos uma opção em Paladar/Olfato')
    }

    // Validar dor
    if (!hasSelectedInCategory([
      'dor_cronica',
      'baixa_sensibilidade_dor',
      'nenhuma_opcao_dor'
    ])) {
      errors.push('Selecione pelo menos uma opção em Dor')
    }

    // Validar equilíbrio
    if (!hasSelectedInCategory([
      'tonturas_frequentes',
      'sensibilidade_desequilibrio',
      'vertigens',
      'quedas_desequilibrio',
      'nenhuma_opcao_equilibrio'
    ])) {
      errors.push('Selecione pelo menos uma opção em Equilíbrio')
    }

    // Validar fala
    if (!hasSelectedInCategory([
      'dificuldades_fala_degluticao',
      'nenhuma_opcao_fala'
    ])) {
      errors.push('Selecione pelo menos uma opção em Fala/Deglutição')
    }

    if (errors.length > 0) {
      alert(`Por favor, preencha os campos obrigatórios:\n\n${errors.join('\n')}`)
      return
    }

    onNext()
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Etapa 9: Capacidades Sensoriais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Visão */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Visão</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="usa_oculos"
                    checked={sensorial.usa_oculos || false}
                    onCheckedChange={(checked) => handleSensorialChange('usa_oculos', checked as boolean)}
                  />
                  <Label 
                    htmlFor="usa_oculos"
                    className="text-sm font-medium"
                  >
                    Usa óculos/lentes de contato
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="condicoes_visuais"
                    checked={sensorial.condicoes_visuais || false}
                    onCheckedChange={(checked) => handleSensorialChange('condicoes_visuais', checked as boolean)}
                  />
                  <Label 
                    htmlFor="condicoes_visuais"
                    className="text-sm font-medium"
                  >
                    Condições conhecidas (catarata, glaucoma, degeneração macular)
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sensibilidade_luz"
                    checked={sensorial.sensibilidade_luz || false}
                    onCheckedChange={(checked) => handleSensorialChange('sensibilidade_luz', checked as boolean)}
                  />
                  <Label 
                    htmlFor="sensibilidade_luz"
                    className="text-sm font-medium"
                  >
                    Sensibilidade à luz
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="visao_noturna"
                    checked={sensorial.visao_noturna || false}
                    onCheckedChange={(checked) => handleSensorialChange('visao_noturna', checked as boolean)}
                  />
                  <Label 
                    htmlFor="visao_noturna"
                    className="text-sm font-medium"
                  >
                    Visão noturna prejudicada
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nenhuma_opcao_visao"
                    checked={sensorial.nenhuma_opcao_visao || false}
                    onCheckedChange={(checked) => handleSensorialChange('nenhuma_opcao_visao', checked as boolean)}
                  />
                  <Label 
                    htmlFor="nenhuma_opcao_visao"
                    className="text-sm font-medium"
                  >
                    Nenhuma das opções
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Audição */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Ear className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Audição</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="usa_aparelho_auditivo"
                    checked={sensorial.usa_aparelho_auditivo || false}
                    onCheckedChange={(checked) => handleSensorialChange('usa_aparelho_auditivo', checked as boolean)}
                  />
                  <Label 
                    htmlFor="usa_aparelho_auditivo"
                    className="text-sm font-medium"
                  >
                    Usa aparelho auditivo
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dificuldade_ouvir"
                    checked={sensorial.dificuldade_ouvir || false}
                    onCheckedChange={(checked) => handleSensorialChange('dificuldade_ouvir', checked as boolean)}
                  />
                  <Label 
                    htmlFor="dificuldade_ouvir"
                    className="text-sm font-medium"
                  >
                    Dificuldade para ouvir conversas
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="zumbidos"
                    checked={sensorial.zumbidos || false}
                    onCheckedChange={(checked) => handleSensorialChange('zumbidos', checked as boolean)}
                  />
                  <Label 
                    htmlFor="zumbidos"
                    className="text-sm font-medium"
                  >
                    Zumbidos ou outros sons
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nenhuma_opcao_auditiva"
                    checked={sensorial.nenhuma_opcao_auditiva || false}
                    onCheckedChange={(checked) => handleSensorialChange('nenhuma_opcao_auditiva', checked as boolean)}
                  />
                  <Label 
                    htmlFor="nenhuma_opcao_auditiva"
                    className="text-sm font-medium"
                  >
                    Nenhuma das opções
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Paladar e Olfato */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Paladar e Olfato</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="usa_protese_dentaria"
                    checked={sensorial.usa_protese_dentaria || false}
                    onCheckedChange={(checked) => handleSensorialChange('usa_protese_dentaria', checked as boolean)}
                  />
                  <Label 
                    htmlFor="usa_protese_dentaria"
                    className="text-sm font-medium"
                  >
                    Usa prótese dentária
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="alteracoes_paladar"
                    checked={sensorial.alteracoes_paladar || false}
                    onCheckedChange={(checked) => handleSensorialChange('alteracoes_paladar', checked as boolean)}
                  />
                  <Label 
                    htmlFor="alteracoes_paladar"
                    className="text-sm font-medium"
                  >
                    Alterações no paladar
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="perda_olfato"
                    checked={sensorial.perda_olfato || false}
                    onCheckedChange={(checked) => handleSensorialChange('perda_olfato', checked as boolean)}
                  />
                  <Label 
                    htmlFor="perda_olfato"
                    className="text-sm font-medium"
                  >
                    Perda ou diminuição do olfato
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nenhuma_opcao_paladar_olfato"
                    checked={sensorial.nenhuma_opcao_paladar_olfato || false}
                    onCheckedChange={(checked) => handleSensorialChange('nenhuma_opcao_paladar_olfato', checked as boolean)}
                  />
                  <Label 
                    htmlFor="nenhuma_opcao_paladar_olfato"
                    className="text-sm font-medium"
                  >
                    Nenhuma das opções
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Sensibilidade à Dor */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Sensibilidade à Dor</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dor_cronica"
                    checked={sensorial.dor_cronica || false}
                    onCheckedChange={(checked) => handleSensorialChange('dor_cronica', checked as boolean)}
                  />
                  <Label 
                    htmlFor="dor_cronica"
                    className="text-sm font-medium"
                  >
                    Dor crônica
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="baixa_sensibilidade_dor"
                    checked={sensorial.baixa_sensibilidade_dor || false}
                    onCheckedChange={(checked) => handleSensorialChange('baixa_sensibilidade_dor', checked as boolean)}
                  />
                  <Label 
                    htmlFor="baixa_sensibilidade_dor"
                    className="text-sm font-medium"
                  >
                    Baixa sensibilidade à dor
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nenhuma_opcao_dor"
                    checked={sensorial.nenhuma_opcao_dor || false}
                    onCheckedChange={(checked) => handleSensorialChange('nenhuma_opcao_dor', checked as boolean)}
                  />
                  <Label 
                    htmlFor="nenhuma_opcao_dor"
                    className="text-sm font-medium"
                  >
                    Nenhuma das opções
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Equilíbrio e Tontura */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Equilíbrio e Tontura</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tonturas_frequentes"
                    checked={sensorial.tonturas_frequentes || false}
                    onCheckedChange={(checked) => handleSensorialChange('tonturas_frequentes', checked as boolean)}
                  />
                  <Label 
                    htmlFor="tonturas_frequentes"
                    className="text-sm font-medium"
                  >
                    Tonturas frequentes
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="sensibilidade_desequilibrio"
                    checked={sensorial.sensibilidade_desequilibrio || false}
                    onCheckedChange={(checked) => handleSensorialChange('sensibilidade_desequilibrio', checked as boolean)}
                  />
                  <Label 
                    htmlFor="sensibilidade_desequilibrio"
                    className="text-sm font-medium"
                  >
                    Sensação de desequilíbrio
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="vertigens"
                    checked={sensorial.vertigens || false}
                    onCheckedChange={(checked) => handleSensorialChange('vertigens', checked as boolean)}
                  />
                  <Label 
                    htmlFor="vertigens"
                    className="text-sm font-medium"
                  >
                    Vertigens
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="quedas_desequilibrio"
                    checked={sensorial.quedas_desequilibrio || false}
                    onCheckedChange={(checked) => handleSensorialChange('quedas_desequilibrio', checked as boolean)}
                  />
                  <Label 
                    htmlFor="quedas_desequilibrio"
                    className="text-sm font-medium"
                  >
                    Quedas por desequilíbrio
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nenhuma_opcao_equilibrio"
                    checked={sensorial.nenhuma_opcao_equilibrio || false}
                    onCheckedChange={(checked) => handleSensorialChange('nenhuma_opcao_equilibrio', checked as boolean)}
                  />
                  <Label 
                    htmlFor="nenhuma_opcao_equilibrio"
                    className="text-sm font-medium"
                  >
                    Nenhuma das opções
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Fala e Deglutição */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Fala e Deglutição</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="dificuldades_fala_degluticao"
                    checked={sensorial.dificuldades_fala_degluticao || false}
                    onCheckedChange={(checked) => handleSensorialChange('dificuldades_fala_degluticao', checked as boolean)}
                  />
                  <Label 
                    htmlFor="dificuldades_fala_degluticao"
                    className="text-sm font-medium"
                  >
                    Dificuldades motoras finas
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="nenhuma_opcao_fala"
                    checked={sensorial.nenhuma_opcao_fala || false}
                    onCheckedChange={(checked) => handleSensorialChange('nenhuma_opcao_fala', checked as boolean)}
                  />
                  <Label 
                    htmlFor="nenhuma_opcao_fala"
                    className="text-sm font-medium"
                  >
                    Nenhuma das opções
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Termo de Veracidade */}
          <div className="border-t pt-6">
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Termo de Veracidade</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Declaro que as informações prestadas nesta avaliação funcional são verdadeiras e foram fornecidas com base no meu conhecimento sobre a capacidade do idoso(a).
              </p>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="veracidade_etapa9"
                  checked={formData.veracidade_etapa9 || false}
                  onCheckedChange={(checked) => onChange('veracidade_etapa9', checked as boolean)}
                  className="!rounded-full data-[state=checked]:!bg-green-600 data-[state=checked]:!border-green-600"
                />
                <Label htmlFor="veracidade_etapa9" className="text-sm font-medium">
                  Declaro que as informações são verdadeiras *
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrev}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Anterior
        </Button>
        <Button onClick={validateAndProceed}>
          Próxima Etapa
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  )
}
